import { Injectable } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { format, parseISO } from "date-fns";
import { ROUTES } from "src/app/utils/routes";
import { ConversionWorkflowStatus } from "src/app/utils/conversion-workflow-status";
import { decimalValidator } from "src/app/utils/validators";
import { StorageService } from "../storage.service";
import {
  LabelTranDetModel,
  LabelTranModel,
  LabelTransactionRequest,
} from "../swagger";

export const CONVERSION_LABEL_TRAN_TYPE_ID = 1;

/** Drop any time/zone suffix, leaving the bare "yyyy-MM-dd" the user picked. */
function stripTime(value: string): string {
  return value.slice(0, 10);
}

/**
 * Normalize a picked date to the midnight-ISO shape the API uses for
 * expiration dates ("2026-10-20T00:00:00") — no timezone, so the date the
 * user picked is the date the backend stores.
 */
function toMidnightIso(value: string): string {
  return `${stripTime(value)}T00:00:00`;
}

export const conversionCreateDraftKey = "conversionDraft:create";
export const conversionUpdateDraftKeyPrefix = "conversionDraft:update:";

/**
 * Drop every conversion draft on the device — used when the user abandons the
 * page and when the session ends, so a shared device never offers one user's
 * unsaved conversion to the next.
 */
export const clearAllConversionDrafts = async (
  storage: StorageService,
): Promise<void> => {
  try {
    await storage.remove(conversionCreateDraftKey);
    const keys = await storage.keys();
    const updateKeys = (keys ?? []).filter((key) =>
      key.startsWith(conversionUpdateDraftKeyPrefix),
    );
    for (const key of updateKeys) {
      await storage.remove(key);
    }
  } catch (e) {
    // Ignore storage errors (quota, private mode, etc.).
  }
};

export type ConversionDraft = {
  meta: {
    labelTranId: number | null;
    labelTranNumber: string;
    savedAt: string;
  };
  form: any;
};

@Injectable()
export class CreateConversionForm {
  form: FormGroup;
  resource: LabelTranModel;
  formattedDate = "";
  dateValue = format(new Date(), "yyyy-MM-dd");
  // "To Exp Date" is optional: empty label + today's date as the picker's
  // starting point, so opening the picker on an unset field isn't blank.
  formattedToExpDate = "";
  toExpDateValue = format(new Date(), "yyyy-MM-dd");
  someThingChange = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private storageService: StorageService,
  ) {}

  get lines(): FormArray {
    return this.form.get("detail") as FormArray;
  }

  get activeLines() {
    return this.lines.controls.filter((c) => c.value.statusId !== 3);
  }

  get conversion(): FormGroup {
    return this.form.get("conversion") as FormGroup;
  }

  get isVoided(): boolean {
    return this.form?.value?.workflowStatusId === ConversionWorkflowStatus.Voided;
  }

  get isSubmitted(): boolean {
    return (
      this.form?.value?.workflowStatusId === ConversionWorkflowStatus.Submitted
    );
  }

  get isReadonly(): boolean {
    // Only Voided records lock down. Pending AND Submitted stay editable so the
    // user can update either state (Pending re-saves, Submit re-posts).
    return this.form?.value?.workflowStatusId === ConversionWorkflowStatus.Voided;
  }

  /**
   * The From/To pair, the To Exp Date and the detail lines freeze once the
   * conversion has been posted — a Submitted record already moved stock, so
   * changing what was converted (or which lines were part of it) would rewrite
   * history. The remaining header fields (template, date, reference, printer)
   * stay editable; Re-Open is the way back to a fully editable record.
   *
   * This is deliberately a presentation guard rather than a control-level
   * disable: Submit stays available in the Submitted state, so the controls
   * must keep contributing to `form.valid` and to the update payload.
   */
  get isLocked(): boolean {
    return this.isReadonly || this.isSubmitted;
  }

  initializeForm(model?: LabelTranModel) {
    this.resource = model;
    const data: any = { ...(model ?? {}) };
    const conv: any = data.conversion ?? {};

    this.form = this.fb.group({
      labelTranId: [data.labelTranId ?? 0],
      labelTranTypeId: [data.labelTranTypeId ?? CONVERSION_LABEL_TRAN_TYPE_ID],
      labelTranTypeSettingId: [data.labelTranTypeSettingId ?? null],
      labelTranNumber: [data.labelTranNumber ?? ""],
      labelTranDate: [data.labelTranDate ?? null, [Validators.required]],
      reference: [data.reference ?? ""],
      labelTemplateId: [data.labelTemplateId ?? null, [Validators.required]],
      labelPrinterName: [data.labelPrinterName ?? ""],
      statusId: [data.statusId ?? 1, [Validators.required]],
      workflowStatusId: [
        data.workflowStatusId ?? ConversionWorkflowStatus.Pending,
        [Validators.required],
      ],
      conversion: this.fb.group({
        labelTranTypeConversionId: [conv.labelTranTypeConversionId ?? 0],
        labelTranId: [conv.labelTranId ?? 0],
        fromLocationId: [conv.fromLocationId ?? null, [Validators.required]],
        fromSiteId: [conv.fromSiteId ?? null, [Validators.required]],
        fromItemNumber: [conv.fromItemNumber ?? "", [Validators.required]],
        toLocationId: [conv.toLocationId ?? null, [Validators.required]],
        toSiteId: [conv.toSiteId ?? null, [Validators.required]],
        toItemNumber: [conv.toItemNumber ?? "", [Validators.required]],
        // Optional: null when the user hasn't picked / has cleared it.
        toExpDate: [conv.toExpDate ?? null],
      }),
      detail: this.fb.array([]),
    });

    if (data && data.labelTranDate) {
      this.dateChanged(data.labelTranDate);
    } else {
      this.formattedDate = "";
      this.dateValue = format(new Date(), "yyyy-MM-dd");
      this.dateChanged(this.dateValue);
    }

    this.syncToExpDateDisplay(conv.toExpDate ?? null);

    if (data.detail && Array.isArray(data.detail)) {
      for (const det of data.detail) {
        this.lines.push(this.buildLineGroup(det));
      }
    }
    this.someThingChange = false;

    if (this.isReadonly) {
      this.form.disable({ emitEvent: false });
    }
  }

  dateChanged(value: string) {
    if (!value) return;
    this.dateValue = value;
    this.form.controls.labelTranDate.setValue(value);
    this.form.controls.labelTranDate.updateValueAndValidity();
    this.formattedDate = format(parseISO(value), "MMM dd yyyy");
    this.someThingChange = true;
  }

  /** Whether any active line already carries a To Exp Date stamp. */
  get hasLines(): boolean {
    return this.activeLines.length > 0;
  }

  /**
   * Set the conversion's To Exp Date. Pass null to clear it. Existing detail
   * lines are deliberately left untouched — only lines added afterwards pick
   * up the new value (see the warning in the detail page).
   */
  toExpDateChanged(value: string | null) {
    const normalized = value ? toMidnightIso(value) : null;
    this.conversion.controls.toExpDate.setValue(normalized);
    this.conversion.controls.toExpDate.updateValueAndValidity();
    this.syncToExpDateDisplay(normalized);
    this.someThingChange = true;
  }

  /**
   * Re-sync the picker to the stored value without touching the form — used
   * when the user backs out of the To Exp Date change warning.
   */
  resetToExpDateDisplay() {
    this.syncToExpDateDisplay(this.conversion.value.toExpDate ?? null);
  }

  /** Keep the picker value + button label in step with the stored value. */
  private syncToExpDateDisplay(value: string | null) {
    if (!value) {
      this.formattedToExpDate = "";
      this.toExpDateValue = format(new Date(), "yyyy-MM-dd");
      return;
    }
    const date = parseISO(stripTime(value));
    this.formattedToExpDate = format(date, "MM/dd/yyyy");
    this.toExpDateValue = format(date, "yyyy-MM-dd");
  }

  private buildLineGroup(det: Partial<LabelTranDetModel> = {}): FormGroup {
    return this.fb.group({
      labelTranDetId: [det.labelTranDetId ?? 0],
      labelTranId: [det.labelTranId ?? 0],
      ordinal: [det.ordinal ?? 0, [Validators.required]],
      itemNumber: [det.itemNumber ?? "", [Validators.required]],
      itemDescription: [det.itemDescription ?? ""],
      // NOT required at the form level: the backend doesn't return uOfM on
      // saved lines, so requiring it here would invalidate a reloaded record
      // and make Pending/Submit silently no-op. The add-item modal enforces
      // UOFM selection when creating/editing a line.
      uOfM: [det.uOfM ?? ""],
      unit: [
        det.unit ?? null,
        [Validators.required, decimalValidator({ required: true, min: 0 })],
      ],
      quantity: [det.quantity ?? 1, [Validators.required]],
      uniqueIdentifierType: [det.uniqueIdentifierType ?? null],
      uniqueIdentifier: [det.uniqueIdentifier ?? ""],
      batchIdentifierType: [det.batchIdentifierType ?? null],
      batchIdentifier: [det.batchIdentifier ?? ""],
      sellByDate: [det.sellByDate ?? null],
      toSellByDate: [det.toSellByDate ?? null],
      barcodeSource: [det.barcodeSource ?? ""],
      reference: [det.reference ?? ""],
      timeStamp: [det.timeStamp ?? new Date().toISOString()],
      statusId: [det.statusId ?? 1, [Validators.required]],
    });
  }

  addLine(payload: Partial<LabelTranDetModel> = {}) {
    const nextOrdinal =
      this.activeLines.reduce(
        (max, c) => Math.max(max, Number(c.value.ordinal) || 0),
        0,
      ) + 1;
    // A line added while the header carries a To Exp Date inherits it, so
    // scanned/manual items go out with the extended expiration. With no header
    // date the expiration isn't being extended, so To mirrors From.
    const toSellByDate =
      payload.toSellByDate ??
      this.conversion?.value?.toExpDate ??
      payload.sellByDate ??
      null;
    this.lines.push(
      this.buildLineGroup({ ...payload, ordinal: nextOrdinal, toSellByDate }),
    );
    this.someThingChange = true;
  }

  updateLine(index: number, payload: Partial<LabelTranDetModel>) {
    const ctrl = this.lines.at(index) as FormGroup;
    if (!ctrl) return;
    ctrl.patchValue(payload);
    this.someThingChange = true;
  }

  removeLine(index: number) {
    const ctrl = this.lines.at(index);
    if (!ctrl) return;
    const detId = ctrl.value.labelTranDetId;
    if (!detId) {
      this.lines.removeAt(index);
    } else {
      ctrl.patchValue({ statusId: 3 });
    }
    this.someThingChange = true;
  }

  getInsertPayload(
    workflowStatusId: ConversionWorkflowStatus,
  ): LabelTransactionRequest {
    const v = this.form.getRawValue();
    return {
      labelTranTypeId: v.labelTranTypeId,
      labelTranDate: v.labelTranDate,
      reference: v.reference,
      labelTemplateId: v.labelTemplateId,
      labelPrinterName: v.labelPrinterName,
      statusId: v.statusId,
      workflowStatusId,
      detail: this.activeLines.map((c) => {
        // For insert, strip the server-assigned IDs.
        const { labelTranDetId, labelTranId, ...rest } = c.value;
        return rest as LabelTranDetModel;
      }),
      conversion: this.buildConversionPayload(false),
    };
  }

  getUpdatePayload(
    workflowStatusId: ConversionWorkflowStatus,
  ): LabelTransactionRequest {
    const v = this.form.getRawValue();
    return {
      labelTranId: v.labelTranId,
      labelTranTypeId: v.labelTranTypeId,
      labelTranTypeSettingId: v.labelTranTypeSettingId,
      labelTranNumber: v.labelTranNumber,
      labelTranDate: v.labelTranDate,
      reference: v.reference,
      labelTemplateId: v.labelTemplateId,
      labelPrinterName: v.labelPrinterName,
      statusId: v.statusId,
      workflowStatusId,
      detail: (this.lines.controls as FormGroup[]).map(
        (c) => c.getRawValue() as LabelTranDetModel,
      ),
      conversion: this.buildConversionPayload(true),
    };
  }

  private buildConversionPayload(includeIds: boolean) {
    const c = this.conversion.getRawValue();
    const payload: any = {
      fromLocationId: c.fromLocationId,
      fromSiteId: c.fromSiteId,
      fromItemNumber: c.fromItemNumber,
      toLocationId: c.toLocationId,
      toSiteId: c.toSiteId,
      toItemNumber: c.toItemNumber,
      // Always sent; null when the user hasn't set / has cleared it.
      toExpDate: c.toExpDate ?? null,
    };
    if (includeIds) {
      payload.labelTranTypeConversionId = c.labelTranTypeConversionId;
      payload.labelTranId = c.labelTranId;
    }
    return payload;
  }

  markUnsaved() {
    this.someThingChange = true;
  }

  markSaved() {
    this.someThingChange = false;
    // `dirty` is the other half of the unsaved-change signal (see
    // hasUnsavedChanges) — leaving it set would keep the draft autosave firing
    // and the leave-page guard tripping after a successful save.
    this.form?.markAsPristine();
  }

  /**
   * Single source of truth for "the user has unsaved work".
   *
   * Two independent signals feed it: controls edited through a value accessor
   * (e.g. the Reference input) set `dirty`, while controls updated
   * programmatically via patchValue — which is most of this form — don't, and
   * their handlers call markUnsaved() instead. Both the draft autosave and the
   * leave-page guard must read the same combination, or one fires without the
   * other and drafts get written for edits the page never warns about.
   */
  get hasUnsavedChanges(): boolean {
    return this.someThingChange || !!this.form?.dirty;
  }

  // ---- Local draft autosave (device storage) -----------------------------

  async getCreateDraft(): Promise<ConversionDraft | null> {
    return await this.readDraft(conversionCreateDraftKey);
  }

  async getUpdateDraft(labelTranId: number): Promise<ConversionDraft | null> {
    if (!labelTranId) return null;
    return await this.readDraft(this.getUpdateDraftKey(labelTranId));
  }

  async saveDraftFromForm(): Promise<void> {
    if (!this.form) return;
    const formValue = this.form.getRawValue();
    const labelTranId = formValue?.labelTranId ? formValue.labelTranId : null;
    const draft: ConversionDraft = {
      meta: {
        labelTranId,
        labelTranNumber: formValue?.labelTranNumber ?? "",
        savedAt: new Date().toISOString(),
      },
      form: formValue,
    };
    const key = labelTranId
      ? this.getUpdateDraftKey(labelTranId)
      : conversionCreateDraftKey;
    await this.writeDraft(key, draft);
  }

  async clearDraftForCurrentForm(): Promise<void> {
    if (!this.form) return;
    const labelTranId = this.form.getRawValue()?.labelTranId;
    if (labelTranId) {
      await this.clearDraft(this.getUpdateDraftKey(labelTranId));
    } else {
      await this.clearDraft(conversionCreateDraftKey);
    }
  }

  restoreFromDraft(draft: ConversionDraft) {
    if (!draft?.form || !this.form) return;
    // A locked record is showing values that were already posted. Patching a
    // stale draft over them would hide what is actually saved, in fields the
    // user cannot correct because the form is disabled.
    if (this.isReadonly) return;
    const f = draft.form;
    this.lines.clear();
    if (Array.isArray(f.detail)) {
      for (const det of f.detail) {
        this.lines.push(this.buildLineGroup(det));
      }
    }
    const { detail, ...header } = f;
    this.form.patchValue(header);
    if (f.labelTranDate) {
      this.dateChanged(f.labelTranDate);
    }
    this.syncToExpDateDisplay(f.conversion?.toExpDate ?? null);
    this.someThingChange = true;
  }

  private getUpdateDraftKey(labelTranId: number): string {
    return `${conversionUpdateDraftKeyPrefix}${labelTranId}`;
  }

  private async readDraft(key: string): Promise<ConversionDraft | null> {
    try {
      const raw = await this.storageService.get(key);
      if (!raw) return null;
      return JSON.parse(raw) as ConversionDraft;
    } catch (e) {
      return null;
    }
  }

  private async writeDraft(key: string, draft: ConversionDraft): Promise<void> {
    try {
      await this.storageService.set(key, JSON.stringify(draft));
    } catch (e) {
      // Ignore storage errors (quota, private mode, etc.).
    }
  }

  private async clearDraft(key: string): Promise<void> {
    try {
      await this.storageService.remove(key);
    } catch (e) {
      // Ignore storage errors (quota, private mode, etc.).
    }
  }

  async backToOperationsPage() {
    await this.router.navigate([ROUTES.OPERATIONS_CONVERSION_PAGE]);
  }
}
